import { FC, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Badge, Box, CheckCircleSolid, HStack, IconButton, SparklesOutline, Spinner, Text, Tooltip, TrashOutline, VStack } from '@bonusly/design-system';

import { BizyProfilePatchFieldError, parseBizyProfilePatchError, useBizyProfile, useBizyUpdateProfileMutation } from '../../queries/bizy-profile';

// ─── Field labels ───────────────────────────────────────────────────────────
//
// Known server field names get curated, human-friendly labels. Any field name
// the server adds in the future falls back to a snake_case → Title Case
// conversion so no FE change is required when `PROFILE_FIELDS` grows.

const FIELD_LABELS: Record<string, string> = {
	bizy_nickname: 'Nickname',
	job_title: 'Job title',
};

const titleCase = (snakeCase: string): string =>
	snakeCase
		.split('_')
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');

const labelFor = (fieldName: string): string => FIELD_LABELS[fieldName] ?? titleCase(fieldName);

// ─── Error message helper ───────────────────────────────────────────────────

const errorMessageFor = (fieldError: BizyProfilePatchFieldError): string => {
	switch (fieldError.reason) {
		case 'rejected':
			return "We couldn't clear that. Try again.";
		case 'invalid_field':
			return 'This field isn\u2019t recognized.';
		case 'too_long':
		default:
			return fieldError.error || 'Failed to clear.';
	}
};

// ─── Profile Field Row ──────────────────────────────────────────────────────

interface ProfileFieldRowProps {
	fieldName: string;
	value: string | null;
	required: boolean;
}

export const ProfileFieldRow: FC<ProfileFieldRowProps> = ({ fieldName, value, required }) => {
	const [serverError, setServerError] = useState<string | null>(null);
	const updateMutation = useBizyUpdateProfileMutation();
	const { track } = useAnalytics();

	const label = labelFor(fieldName);
	const hasValue = value !== null && value.trim() !== '';

	const handleClear = () => {
		setServerError(null);
		updateMutation.mutate(
			{ [fieldName]: null },
			{
				onSuccess: () => {
					track(AnalyticsEvent.BizyProfile.FieldCleared, { field: fieldName });
				},
				onError: (error) => {
					const fieldError = parseBizyProfilePatchError(error);
					if (fieldError && fieldError.field === fieldName) {
						setServerError(errorMessageFor(fieldError));
						track(AnalyticsEvent.BizyProfile.FieldUpdateRejected, {
							field: fieldName,
							reason: fieldError.reason,
						});
					} else {
						setServerError('Failed to clear. Try again.');
					}
				},
			}
		);
	};

	return (
		<Box>
			<HStack spacing={2} mb={1}>
				<Text fontSize="xs" fontWeight="medium" color="text.soft">
					{label}
				</Text>
				{required && (
					<Badge colorScheme="gray" fontSize="2xs" variant="subtle">
						Required
					</Badge>
				)}
			</HStack>

			<HStack bg="secondary.bgAlpha" borderRadius="md" px={3} py={2} justify="space-between" spacing={2} minH={9} cursor="default">
				{hasValue ? (
					<Text fontSize="sm" color="text.default" wordBreak="break-word" cursor="default">
						{value}
					</Text>
				) : (
					<Text fontSize="sm" color="text.softer" fontStyle="italic" cursor="default">
						Ask Bizy to set this
					</Text>
				)}
				{hasValue && (
					<Tooltip label="Clear value" hasArrow={false}>
						<IconButton
							aria-label={`Clear ${label}`}
							icon={<TrashOutline size="xs" />}
							size="xs"
							variant="ghost"
							color="secondary.fg"
							onClick={handleClear}
							isLoading={updateMutation.isLoading}
						/>
					</Tooltip>
				)}
			</HStack>

			{serverError && (
				<Text fontSize="xs" color="error.text" mt={1}>
					{serverError}
				</Text>
			)}
		</Box>
	);
};

// ─── Profile Section ────────────────────────────────────────────────────────

export const ProfileSection: FC = () => {
	const { data: profile, isLoading } = useBizyProfile();

	return (
		<Box>
			<HStack spacing={2} mb={2} justify="space-between">
				<Text fontSize="sm" fontWeight="semibold" color="text.default">
					Profile
				</Text>
				{profile?.required_complete && (
					<HStack spacing={1} color="success.text">
						<CheckCircleSolid boxSize={3.5} />
						<Text fontSize="xs" fontWeight="medium">
							Complete
						</Text>
					</HStack>
				)}
			</HStack>
			<HStack spacing={1.5} mb={3} color="text.softer" align="flex-start">
				<Box flexShrink={0} mt="2px">
					<SparklesOutline boxSize={3} />
				</Box>
				<Text fontSize="xs">Just chat with Bizy to set or update these. You can clear a value here to start fresh.</Text>
			</HStack>

			{isLoading ? (
				<HStack justify="center" py={4}>
					<Spinner size="sm" />
				</HStack>
			) : profile ? (
				<VStack spacing={3} align="stretch">
					{profile.required_fields.map((fieldName) => (
						<ProfileFieldRow
							key={fieldName}
							fieldName={fieldName}
							value={profile.fields[fieldName] ?? null}
							required={profile.required_fields.includes(fieldName)}
						/>
					))}
				</VStack>
			) : (
				<Text fontSize="sm" color="text.softer">
					Unable to load profile.
				</Text>
			)}
		</Box>
	);
};
